import { defineStore } from 'pinia';
import { setMeta } from "~/utils/meta";
import * as qs from "qs";
import { ProductDetailEntity } from "~/entities/shop/ProductDetailEntity";
import { RecipeIngredientType } from "~/types/recipe/RecipeIngredientType";
import { RecipeCuisineType } from "~/types/recipe/RecipeCuisineType";
import { H3Error } from "h3";
import { IngredientEntity } from "~/entities/recipe/IngredientEntity";
import { CuisineEntity } from "~/entities/recipe/CuisineEntity";

export type useFilterRecipeStoreType = {
    ingredients?: RecipeIngredientType[],
    selectedIngredients?: RecipeIngredientType[],
    cuisines?: RecipeCuisineType[],
    selectedCuisines?: RecipeCuisineType[],
    error?: object | H3Error,
};

export const useFilterRecipeStore = defineStore({
    id: 'useFilterRecipeStore',
    state: (): useFilterRecipeStoreType => ({
        ingredients: undefined,
        selectedIngredients: undefined,
        cuisines: undefined,
        selectedCuisines: undefined,
        error: undefined,
    }),
    getters: {
        getFilterIngredients(state: useFilterRecipeStoreType) {
            if (!state.ingredients) {
                return [];
            }
            return state.ingredients.map(item => ({
                value: item.id,
                label: item.title,
            }));
        },

        getSelectedFilterIngredients(state: useFilterRecipeStoreType) {
            if (!state.selectedIngredients) {
                return [];
            }
            return state.selectedIngredients.map(item => ({
                value: item.id,
                label: item.title,
            }));
        },

        getFilterCuisines(state: useFilterRecipeStoreType) {
            if (!state.cuisines) {
                return [];
            }
            return state.cuisines.map(item => ({
                value: item.id,
                label: `${item.flag} ${item.title}`,
            }));
        },

        getSelectedFilterCuisines(state: useFilterRecipeStoreType) {
            if (!state.selectedCuisines) {
                return [];
            }
            return state.selectedCuisines.map(item => ({
                value: item.id,
                label: item.title,
            }));
        },
    },
    actions: {
        clear() {
            this.ingredients = undefined;
            this.selectedIngredients = undefined;
            this.cuisines = undefined;
            this.selectedCuisines = undefined;
            this.error = undefined;
        },

        async fetchIngredients(categorySlug?: string, searchTitle?: string) {
            try {
                const query = {
                    fields: [
                        'id',
                        'title',
                    ],
                    sort: ['title:asc'],
                };

                let getPath = '/recipe-ingredients/use-recipes';
                if (categorySlug) {
                    getPath += `/${categorySlug}`;
                }

                if (searchTitle) {
                    query.filters = {
                        title: {
                            $containsi: searchTitle,
                        },
                    };
                }

                const response = await this.$useNuxtApp.axios.get(`${getPath}?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                if (!response?.data?.data.length) {
                    this.ingredients = [];
                    return true;
                }

                this.ingredients = response?.data?.data.map(item => IngredientEntity(item));
            } catch (error) {
                console.log(error.message);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        },

        async fetchSelectedIngredients(ids: number[]) {
            try {
                if (!ids.length) {
                    this.selectedIngredients = [];
                    return true;
                }

                const query = {
                    fields: [
                        'id',
                        'title',
                    ],
                    sort: ['title:asc'],
                    filters: {
                        id: {
                            $in: ids,
                        }
                    },
                };

                const response = await this.$useNuxtApp.axios.get(`/recipe-ingredients?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || !response?.data?.data.length || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                this.selectedIngredients = response?.data?.data.map(item => IngredientEntity(item));
            } catch (error) {
                console.log(error.message);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        },

        async fetchCuisines(categorySlug?: string, searchTitle?: string) {
            try {
                const query = {
                    fields: [
                        'id',
                        'title',
                        'flag',
                    ],
                    sort: ['title:asc'],
                };

                let getPath = '/recipe-country-of-origins/use-recipes';
                if (categorySlug) {
                    getPath += `/${categorySlug}`;
                }

                if (searchTitle) {
                    query.filters = {
                        title: {
                            $containsi: searchTitle,
                        },
                    };
                }

                const response = await this.$useNuxtApp.axios.get(`${getPath}?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                if (!response?.data?.data.length) {
                    this.cuisines = [];
                    return true;
                }

                this.cuisines = response?.data?.data.map(item => CuisineEntity(item));
            } catch (error) {
                console.log(error.message);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        },

        async fetchSelectedCuisines(ids: number[]) {
            try {
                if (!ids.length) {
                    this.selectedCuisines = [];
                    return true;
                }

                const query = {
                    fields: [
                        'id',
                        'title',
                        'flag',
                    ],
                    sort: ['title:asc'],
                    filters: {
                        id: {
                            $in: ids,
                        }
                    },
                };

                const response = await this.$useNuxtApp.axios.get(`/recipe-country-of-origins?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || !response?.data?.data.length || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                this.selectedCuisines = response?.data?.data.map(item => CuisineEntity(item));
            } catch (error) {
                console.log(error.message);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        }
    },
});
